// ─────────────────────────────────────────────────────────────────────────────
// ENGLISH MESSAGES — uPBo Next-Gen Web Creation Platform
// ─────────────────────────────────────────────────────────────────────────────

export const en = {
  // ── Shell & Navigation ─────────────────────────────────────────────────────
  'nav.overview': 'Overview',
  'nav.settings': 'Settings',
  'nav.signIn': 'Sign in',
  'nav.signUp': 'Sign up',
  'nav.menu': 'Menu',
  'nav.home': 'Home',
  'nav.docs': 'Docs',
  'nav.pricing': 'Pricing',
  'nav.dashboard': 'Dashboard',
  'nav.account': 'Account',
  'nav.login': 'Log In',
  'nav.register': 'Get Started',
  'nav.logout': 'Sign Out',
  'nav.theme': 'Theme',

  // ── Footer ─────────────────────────────────────────────────────────────────
  'footer.tagline': 'Launch next-generation web applications instantly with cinematic space-age visuals.',
  'footer.product': 'Product',
  'footer.product.overview': 'Overview',
  'footer.product.createAccount': 'Create Account',
  'footer.product.signIn': 'Sign In',
  'footer.resources': 'Resources',
  'footer.account': 'Account',
  'footer.account.account': 'Account',
  'footer.account.settings': 'Settings',
  'footer.builtWith': 'Crafted on the uPBo web creation platform.',

  // ── Theme & Language ───────────────────────────────────────────────────────
  'theme.label': 'Theme',
  'theme.light': 'Light',
  'theme.dark': 'Dark',
  'theme.system': 'System',
  'language.label': 'Language',

  // ── Home Landing: Speed, Aesthetics & Fluid Motion ─────────────────────────
  'home.status.connected': 'Infrastructure Online',
  'home.status.missing': 'Configuration Needed',
  'home.cta.createAccount': 'Create account',
  'home.cta.tryBelow': 'Try it below',
  'home.arch.shared': 'shared by both halves',
  'home.hero.title': 'Turn Any Vision Into A Stunning Website In Seconds',
  'home.hero.subtitle':
    'Next-generation web creation platform combining blazing speed, studio aesthetic standards, cinematic 3D space motion, and seamless mobile responsiveness.',
  'home.hero.startBuilding': 'Start Building Free',
  'home.hero.exploreShowcase': 'Explore Showcase',
  'home.hero.inputPlaceholder': 'Describe your dream website...',
  'home.hero.createSite': 'Generate Website',
  'home.hero.generating': 'Synthesizing experience...',
  'home.hero.promptSuggestion1': 'Artisan coffee brand with warm tones',
  'home.hero.promptSuggestion2': 'Futuristic tech studio with luminous glow',
  'home.hero.promptSuggestion3': 'Architecture portfolio with smooth motion',
  'home.hero.readyBadge': 'Space 3D • 60 FPS Motion',
  'home.hero.metricSpeed': '1.8s Creation',
  'home.hero.metricFps': '60 FPS Motion',
  'home.hero.metricStudio': 'Studio Aesthetics',
  'home.hero.metricMobile': '100% Responsive',

  // ── 4 Pillars of Excellence ────────────────────────────────────────────────
  'home.pillars.title': 'Extreme Velocity, Studio Aesthetics & Fluid Motion',
  'home.pillars.body':
    'Every capability required to launch an arresting website that captivates audiences from their first interaction.',
  'home.pillars.speed.title': 'Instant Synthesis',
  'home.pillars.speed.body':
    'From natural language concept to fully interactive site in seconds. Eliminating hundreds of hours of manual layout.',
  'home.pillars.motion.title': 'Cinematic Space Motion',
  'home.pillars.motion.body':
    'Interactive 3D planetary globe, orbiting satellites, luminous atmospheric rays, and buttery-smooth 60 FPS physics.',
  'home.pillars.design.title': 'Deep Space Luxury',
  'home.pillars.design.body':
    'Curated Deep Space Obsidian & Celestial Cyan palettes, frosted glass radiance, and golden visual ratios.',
  'home.pillars.responsive.title': 'Engineered For Mobile',
  'home.pillars.responsive.body':
    'Tailored typography, swipeable carousels, and thumb-friendly touch targets that feel native on iOS and Android.',

  // Legacy pillar keys for compatibility
  'home.pillar.oneCodebase.title': 'One codebase',
  'home.pillar.oneCodebase.body': 'Client and server unified in a single typed codebase.',
  'home.pillar.ownDatabase.title': 'Your own database',
  'home.pillar.ownDatabase.body': 'Standard Postgres connectivity with no vendor lock-in.',
  'home.pillar.accounts.title': 'Accounts included',
  'home.pillar.accounts.body': 'Hardened session cookies and user authentication.',
  'home.pillar.aiReadable.title': 'Readable by AI',
  'home.pillar.aiReadable.body': 'Dynamic sitemaps and autonomous search readiness.',

  'home.steps.title': 'From concept to running website',
  'home.step.describe.title': 'Describe in words',
  'home.step.describe.body': 'Natural language synthesis of layout and behavior.',
  'home.step.build.title': 'Builds in sandbox',
  'home.step.build.body': 'Automated code repair and verification.',
  'home.step.ship.title': 'Ships immediately',
  'home.step.ship.body': 'Deploys globally to high-speed edge infrastructure.',

  'home.demo.title': 'Live data layer',
  'home.demo.body': 'Interactive state mutations with real-time feedback.',
  'home.demo.filesLabel': 'Layered architecture:',
  'home.demo.llmsLink': 'See llms.txt',

  'demo.empty.signedIn': 'Nothing here yet. Add an item below.',
  'demo.empty.signedOut': 'Nothing here yet. Sign in to add the first item.',
  'demo.newItemLabel': 'New item title',
  'demo.add': 'Add',
  'demo.signInToAdd': 'Sign in to add an item.',
  'demo.delete': 'Delete {title}',

  // ── Showcase Section ───────────────────────────────────────────────────────
  'home.showcase.title': 'Tailored Design Directions',
  'home.showcase.subtitle':
    'Select the visual identity and tonal direction that best represents your vision.',
  'home.showcase.techMinimal': 'Tech Minimal',
  'home.showcase.luxuryEditorial': 'Luxury Editorial',
  'home.showcase.creativeDynamic': 'Creative Dynamic',
  'home.showcase.techDesc':
    'Sharp geometries, deep dark surfaces with radiant cyan luminescence, engineered for cutting-edge software and hardware products.',
  'home.showcase.luxuryDesc':
    'High-contrast serif headings, expansive negative space, and champagne starlight tones for premium brands.',
  'home.showcase.creativeDesc':
    'Bold gradient overlays, energetic interactive pills, and playful dynamic components that ignite curiosity.',

  'home.features.ready': 'Production Ready',
  'home.features.ready.desc': 'Edge-optimized architecture with hardened security and sub-second cold starts globally.',
  'home.features.auth': 'Secure Authentication',
  'home.features.auth.desc': 'Hardened credential hashing with instant Google Sign-In support in a single tap.',
  'home.features.crud': 'Instant Feedback',
  'home.features.crud.desc': 'Real-time state mutations with optimistic UI updates and reactive caching.',

  'home.crud.title': 'Active Workspace Projects',
  'home.crud.subtitle': 'Try adding or removing a project to experience instantaneous reactive state mutations.',
  'home.crud.placeholder': 'Enter a new project or site title...',
  'home.crud.add': 'Add Project',
  'home.crud.empty': 'No projects added yet. Create your first workspace item above!',

  'home.cta.title': 'Ready To Launch Your Website?',
  'home.cta.body': 'Experience the modern web creation platform that turns creative vision into live reality.',
  'home.cta.action': 'Start Building Now',
  'home.cta.contact': 'Read Architecture',

  // ── Shared Form Fields ─────────────────────────────────────────────────────
  'field.email': 'Email',
  'field.emailPlaceholder': 'you@example.com',
  'field.password': 'Password',
  'field.currentPassword': 'Current password',
  'field.newPassword': 'New password',
  'field.displayName': 'Display name',
  'field.optional': 'Optional',

  // ── Authentication ─────────────────────────────────────────────────────────
  'auth.loginTitle': 'Welcome back',
  'auth.loginSubtitle': 'Sign in to your uPBo account to continue',
  'auth.registerTitle': 'Create your uPBo account',
  'auth.registerSubtitle': 'Start building stunning web applications in seconds',
  'auth.email': 'Email address',
  'auth.password': 'Password',
  'auth.name': 'Full name',
  'auth.submitLogin': 'Sign In',
  'auth.submitRegister': 'Create Account',
  'auth.orContinueWith': 'Or continue with',
  'auth.continueWithGoogle': 'Sign in with Google',
  'auth.alreadyHaveAccount': 'Already have an account?',
  'auth.dontHaveAccount': "Don't have an account?",
  'auth.signInLink': 'Sign In',
  'auth.signUpLink': 'Sign Up Free',
  'auth.googleFailed': 'Google authentication failed. Please try again.',
  'auth.invalidCredentials': 'Invalid email or password',
  'auth.emailInUse': 'Email is already registered',
  'auth.weakPassword': 'Password must be at least 8 characters',
  'auth.panel.title': 'Own your identity and data',
  'auth.panel.body': 'Full-stack architecture where accounts and sessions are under your complete control.',
  'auth.orDivider': 'or',
  'auth.signUpWithGoogle': 'Sign up with Google',

  // ── Sign In ────────────────────────────────────────────────────────────────
  'login.title': 'Sign in',
  'login.subtitle': 'Continue with your account',
  'login.highlight.cookie':
    'The session lives in an HttpOnly cookie — JavaScript cannot read it, so an XSS bug cannot steal the account.',
  'login.highlight.hash': 'Passwords are hashed with PBKDF2 over 100,000 rounds, each with its own salt.',
  'login.highlight.ownData': 'The data sits in your own database, with no third-party provider in between.',
  'login.noAccount': 'No account yet?',
  'login.submit': 'Sign in',
  'login.failed': 'Could not sign in. Try again.',

  // ── Register ───────────────────────────────────────────────────────────────
  'register.title': 'Create account',
  'register.subtitle': 'Open an account on your website',
  'register.highlight.firstIsAdmin':
    'The FIRST person to register becomes the administrator of the website — no hand-written SQL needed.',
  'register.highlight.noProvider':
    'No verification step through an outside provider: the account lives in your database.',
  'register.highlight.selfServe': 'Display name and password are editable from the account page.',
  'register.haveAccount': 'Already have an account?',
  'register.passwordHint': 'At least {count} characters.',
  'register.passwordTooShort': 'Password must be at least {count} characters',
  'register.submit': 'Create account',
  'register.failed': 'Could not create the account. Try again.',

  // ── Account ────────────────────────────────────────────────────────────────
  'account.title': 'Account',
  'account.eyebrow': 'Account Overview',
  'account.body': 'Manage your personal profile and security sessions',
  'account.subtitle': 'Manage your personal profile and security sessions',
  'account.email': 'Email',
  'account.name': 'Display Name',
  'account.created': 'Member Since',
  'account.security': 'Security',
  'account.logoutAll': 'Sign Out All Devices',
  'account.activeSessions': 'Active Sessions',
  'account.adminBadge': 'Administrator',
  'account.signOut': 'Sign out',
  'account.profile.title': 'Profile',
  'account.profile.description': 'Your display name on this website.',
  'account.profile.saved': 'Profile saved',
  'account.profile.failed': 'Could not save. Try again.',
  'account.save': 'Save',
  'account.password.title': 'Password',
  'account.password.description': 'Your current password is required to confirm it is you.',
  'account.password.changed': 'Password changed',
  'account.password.failed': 'Could not change the password. Try again.',
  'account.password.submit': 'Change password',

  // ── Guarded pages ──────────────────────────────────────────────────────────
  'guard.forbidden.title': 'Not enough permission',
  'guard.forbidden.body': 'This page is for the administrators of the website.',

  // ── Not found ──────────────────────────────────────────────────────────────
  'notFound.title': 'No such page',
  'notFound.body': 'The address you opened does not exist, or it was renamed.',
  'notFound.home': 'Back to home',

  // ── Settings ───────────────────────────────────────────────────────────────
  'settings.title': 'Settings',
  'settings.eyebrow': 'Platform Configuration',
  'settings.body': 'Fine-tune identity, SEO and search crawler policies.',
  'settings.subtitle': 'Changes here take effect immediately — no redeploy needed.',
  'settings.appearance': 'Appearance',
  'settings.language': 'Language',
  'settings.tab.seo': 'SEO & AI Search',
  'settings.tab.ai': 'AI crawlers',
  'settings.saved': 'Saved. robots.txt, sitemap.xml and llms.txt are already updated.',
  'settings.unavailable':
    'Could not read the settings — usually because DATABASE_URL is not set. The website keeps running on the defaults in shared/seo.config.ts.',
  'settings.identity.title': 'Website identity',
  'settings.identity.description':
    'These are the exact sentences Google and the AI assistants quote when they introduce your website.',
  'settings.siteName.label': 'Website name',
  'settings.siteName.hint': 'Goes into the browser title, share cards and structured data.',
  'settings.description.label': 'Description',
  'settings.description.hint': 'One or two sentences. Write for a reader, do not stuff keywords.',
  'settings.siteUrl.label': 'Primary domain',
  'settings.siteUrl.hint': 'For example https://yourcompany.com — leave empty to use the domain being visited.',
  'settings.keywords.label': 'Main topics',
  'settings.keywords.hint': 'Comma separated. Goes into llms.txt so AI knows what the website is about.',
  'settings.sameAs.label': 'Social links',
  'settings.sameAs.hint': 'Comma separated. Helps AI connect the website to the right brand.',
  'settings.ai.title': 'Let AI read the website',
  'settings.ai.description':
    'The new marketing channel: when someone asks ChatGPT, Claude or Perplexity about your field, the website can only be cited if those bots can read it.',
  'settings.ai.allow.title': 'Allow AI crawlers',
  'settings.ai.allow.body':
    'Opens robots.txt to GPTBot, ClaudeBot, PerplexityBot, Google-Extended and more than fifteen others. Turning it off blocks them outright.',
  'settings.ai.prerender.title': 'Serve rendered content to bots',
  'settings.ai.prerender.body':
    'CRITICAL for a SPA. Most AI crawlers do not run JavaScript, so with this off they see an empty page. On, the server injects the real content into the HTML it sends to bots — real visitors are unaffected.',
  'settings.ai.filesLabel': 'Files AI can read right now',
  'settings.save': 'Save changes',
  'settings.saving': 'Saving…',

  // ── Common ─────────────────────────────────────────────────────────────────
  'common.loading': 'Loading...',
  'common.save': 'Save Changes',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.error': 'An unexpected error occurred',
  'common.success': 'Operation completed successfully',

  // ── Errors ─────────────────────────────────────────────────────────────────
  'error.auth.email_invalid': 'That email address is not valid',
  'error.auth.password_too_short': 'That password is too short',
  'error.auth.email_taken': 'That email is already registered',
  'error.auth.missing_credentials': 'Email or password is missing',
  'error.auth.invalid_credentials': 'Wrong email or password',
  'error.auth.current_password_wrong': 'The current password is wrong',
  'error.auth.unauthenticated': 'You need to sign in',
  'error.auth.forbidden': 'You are not allowed to do this',
  'error.db.not_configured': 'The database is not configured yet. Set the DATABASE_URL environment variable.',
  'error.http.not_found': 'No such endpoint',
  'error.http.method_not_allowed': 'That method is not supported on this path',
  'error.http.server_error': 'Server error',
  'error.posts.title_required': 'A title is required',
  'error.posts.not_found': 'Not found',
  'error.posts.not_owner': 'This row does not belong to you',
} as const

export type MessageKey = keyof typeof en
